import React, { useState, useEffect, useRef } from 'react';
import styles from "./MultiSelectcustomDropdown.module.css";
import { IoCaretDownOutline } from 'react-icons/io5';
import { normalizeOption } from '../../helpers';
import { Body, BodyColor, BodyType } from '../typography/Body';

interface DropdownOption {
    label: string;
    key: string;
    children?: DropdownOption[];
    isSelected?: boolean;
}

interface DropdownProps {
    options: DropdownOption[];
    selectedValues: string[];
    onChange: (values: string[]) => void;
    labelKey?: string;
    valueKey?: string;
    disabled?: boolean;
    classname?: string;
    id?: string;
    style?: any;
    placeholder?: string;
    name?: string;
    type?: string;
}

export const MultiSelectCustomDropdown = ({
    options,
    selectedValues,
    onChange,
    labelKey = "label",
    valueKey = "key",
    disabled = false,
    classname = "",
    id,
    style = {},
    placeholder = "Select",
    name,
    type
}: DropdownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [expandedParents, setExpandedParents] = useState<string[]>([]);
    const dropdownRef = useRef<HTMLDivElement | null>(null);
    const [internalSelectedValues, setInternalSelectedValues] = useState<string[]>([]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleParentExpand = (value: string) => {
        setExpandedParents(prev =>
            prev.includes(value) ? [] : [value]
        );
    };

    useEffect(() => {
        setInternalSelectedValues(selectedValues);
    }, [selectedValues]);

    // useEffect(() => {
    //     const getSelectedValues = (opts: DropdownOption[]): string[] => {
    //         let selected: string[] = [];
    //         for (const opt of opts) {
    //             if (opt.isSelected) selected.push(opt.key);
    //             if (opt.children) selected.push(...getSelectedValues(opt.children));
    //         }
    //         return selected;
    //     };

    //     const initiallySelected = getSelectedValues(options);

    //     if (initiallySelected.length > 0) {
    //         const merged = Array.from(new Set([...selectedValues, ...initiallySelected]));

    //         if (!isFirstRender.current) {
    //             onChange(merged);
    //         }
    //     }
    // }, [options]);

    useEffect(() => {
        if(type=="radio") return
        const getSelectedValues = (opts: DropdownOption[]): string[] => {
            let selected: string[] = [];
            for (const opt of opts) {
                if (opt.isSelected) selected.push(opt.key);
                if (opt.children) selected.push(...getSelectedValues(opt.children));
            }
            return selected;
        };

        const initial = getSelectedValues(options);
        setInternalSelectedValues(initial);
    }, [options]);



    useEffect(() => {
        const updated = new Set([...selectedValues]);

        const checkAndAddParents = (opts: DropdownOption[]) => {
            for (const opt of opts) {
                if (opt.children?.some(child => selectedValues.includes(child.key))) {
                    updated.add(opt.key);
                }
                if (opt.children) {
                    checkAndAddParents(opt.children);
                }
            }
        };

        checkAndAddParents(options);

        if (updated.size !== selectedValues.length) {
            onChange([...updated]);
        }
    }, [selectedValues, options]);



    const handleCheckboxChange = (value: string, parentKey?: string, children?: DropdownOption[]) => {
        let updated = [...internalSelectedValues];
        const isSelected = internalSelectedValues.includes(value);

        if (type === 'radio') {
            setInternalSelectedValues([value]);
            onChange([value]);
            return;
        }

        if (children && children.length > 0) {
            const childKeys = children.map(child => child.key);
            if (isSelected) {
                updated = updated.filter(v => v !== value && !childKeys.includes(v));
            } else {
                updated.push(value, ...childKeys);
            }
        } else {
            if (isSelected) {
                updated = updated.filter(v => v !== value);
            } else {
                updated.push(value);
            }

            // 🔁 Recalculate and clean up invalid parents based on active/closed children
            if (parentKey) {
                const parent = findOptionByKey(options, parentKey);
                if (parent && parent.children) {
                    const allChildKeys = parent.children.map(c => c.key);
                    const selectedChildren = allChildKeys.filter(k => updated.includes(k));

                    // Check for presence of both 'active' and 'closed'
                    const hasActive = selectedChildren.some(k => k.endsWith('_active'));
                    const hasClosed = selectedChildren.some(k => k.endsWith('_closed'));

                    if (!hasActive && !hasClosed) {
                        // ❌ Remove parent and all children if both are missing
                        updated = updated.filter(v => v !== parentKey && !allChildKeys.includes(v));
                    } else {
                        // ✅ Keep parent if at least one is present
                        updated.push(parentKey);
                    }
                }
            }
        }

        const uniqueUpdated = [...new Set(updated)];
        setInternalSelectedValues(uniqueUpdated);
        onChange(uniqueUpdated);
    };



    const findOptionByKey = (options: DropdownOption[], key: string): DropdownOption | null => {
        for (const option of options) {
            if (option.key === key) return option;
            if (option.children) {
                const found = findOptionByKey(option.children, key);
                if (found) return found;
            }
        }
        return null;
    };

    const isChildDisabled = (option: DropdownOption, parentKey?: string) => {
        if (!parentKey || !option.key.endsWith('_growth') && !option.key.endsWith('_performance')) return false;

        const parent = findOptionByKey(options, parentKey);
        if (!parent || !parent.children) return false;

        const selectedKeys = internalSelectedValues;
        const selectedChildren = parent.children.map(c => c.key).filter(k => selectedKeys.includes(k));

        const hasActive = selectedChildren.some(k => k.endsWith('_active'));
        const hasClosed = selectedChildren.some(k => k.endsWith('_closed'));

        return !(hasActive || hasClosed);
    };


    const renderOptions = (options: DropdownOption[], level = 0, parentKey?: string) => {
        return options.map(option => {
            const isParent = Array.isArray(option.children);
            const isExpanded = expandedParents.includes(option.key);
            const isChecked = internalSelectedValues.includes(option.key);

            return (
                <>
                    <div key={option.key} className={styles.optionGroup}>
                        <div className={styles.option}>
                            <input
                                type={type}
                                onChange={() => handleCheckboxChange(option.key, parentKey, option.children)}
                                checked={isChecked}
                                disabled={isChildDisabled(option, parentKey)}
                            />
                            <Body type={BodyType.p3} color={BodyColor.secondary} classname="ms-1">
                                {option.label}
                            </Body>

                            {isParent && type !== 'radio' && (
                                <span
                                    className={`material-symbols-outlined fs-18 cursor-pointer fw-bold color-black-5 ${styles.expandToggle}`}
                                    onClick={() => toggleParentExpand(option.key)}
                                >
                                    {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                </span>
                            )}
                        </div>
                        {isParent && isExpanded && type !== 'radio' && (
                            <div className={styles.children}>
                                {renderOptions(option.children!, level + 1, option.key)}
                            </div>
                        )}
                    </div>
                    {/* Add <hr /> after parent and its children */}
                    {isParent && isExpanded && <hr className="mx-3" />}
                     {/* Add <hr /> after  children closed */}
                     { level === 1 && option.label === "Closed" &&<hr className="mx-3" />}


                </>
            );
        });
    };


    const selectedLabels = options
        .filter(option => internalSelectedValues.includes(option.key))
        .map(option => option.label);


    return (
        <div
            ref={dropdownRef}
            className={`${styles.dropdownContainer} ${classname}`}
            style={style}
            id={id}
        >
            <div
                className={`${styles.dropdownHeader}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
                style={{
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1
                }}
            >
                <Body type={BodyType.p3} color={BodyColor.secondary}>
                    {selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder}
                </Body>
                <span className="material-symbols-outlined fs-18 icon-gray-0">
                    {isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                </span>
            </div>
            {isOpen && (
                <div className={styles.dropdownList}>
                    {renderOptions(options)}
                </div>
            )}
        </div>
    );
};
